import { useContext, useState, useEffect } from "react";
import { Button, Input, Form, Select, DatePicker } from "antd";
import dayjs from "dayjs";

import type { SelectProps } from "antd";
import { useFormContext } from "../../hooks/useFormContext";
import { AuthContext } from "../../context/AuthContext";
import { getCompanyUsersApi } from "../../services/User/UserApis";
import WizardStepLayout from "./WizardStepLayout";

interface StepProps {
  form?: any;
  handleAge: Function;
  gotoStep: Function;
  current: number;
}

const genderOpt: SelectProps["options"] = [
  {
    name: "Gender",
    label: "Male",
    value: "Male",
  },
  {
    name: "Gender",
    label: "Female",
    value: "Female",
  },
];

const identityTypeOpt: SelectProps["options"] = [
  {
    name: "Identity",
    label: "South African ID",
    value: 1,
  },
  {
    name: "Identity",
    label: "South African Passport",
    value: 2,
  },
];

const titleOpt: SelectProps["options"] = [
  {
    name: "Title",
    label: "Mr",
    value: "Mr",
  },
  {
    name: "Title",
    label: "Ms",
    value: "Ms",
  },
  {
    name: "Title",
    label: "Mx",
    value: "Mx",
  },
  {
    name: "Title",
    label: "Mrs",
    value: "Mrs",
  },
  {
    name: "Title",
    label: "Miss",
    value: "Miss",
  },
  {
    name: "Title",
    label: "Madam",
    value: "Madam",
  },
];

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 8, span: 8 },
};

const PersonalInfo: React.FC<StepProps> = ({
  form,
  gotoStep,
  current,
  handleAge,
}) => {
  const { setPersonalInfo } = useFormContext();
  const { currentGroup, currentUser } = useContext(AuthContext);
  const [identity, setIdentity] = useState(1);
  const [gender, setGender] = useState<any>({ name: "", value: "" });
  const [users, setUsers] = useState<SelectProps["options"]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  
  const onChangeIdentity = (e: number) => {
    setIdentity(e);
  };

  const extractDateOfBirth = (idNumber: string) => {
    const year = parseInt(idNumber.substring(0, 2), 10);
    const month = parseInt(idNumber.substring(2, 4), 10);
    const day = parseInt(idNumber.substring(4, 6), 10);

    // Adjust the year for the 1900s or 2000s
    const currentYear = new Date().getFullYear();
    const birthYear = year + (year < currentYear % 100 ? 2000 : 1900);

    return new Date(birthYear, month - 1, day); // Month is zero-based
  };

  const onChangeId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id: string = e.target.value;
    const checkGender = id.substring(6, 10);
    const gender = Number(checkGender) < 5000 ? "Female" : "Male";

    // Update the form value after determining gender
    form.setFieldsValue({
      Gender: gender,
    });

    // Set gender state (this won't trigger a re-render that causes issues)
    setGender(gender);

    // Handle the date of birth calculation based on the ID
    handleDateOfBirth(id);
  };

  const handleDateOfBirth = (idNumber: string) => {
    if (idNumber.length !== 13) return;

    const dateOfBirth = extractDateOfBirth(idNumber);

    form.setFieldsValue({
      DateOfBirth: dayjs(dateOfBirth).add(2, "hour"),
    });

    handleAge(dateOfBirth);
  };

  const idNumber = Form.useWatch("IdNumber", form);

  useEffect(() => {
    if (idNumber?.length === 13) {
      handleDateOfBirth(idNumber);
    }
  }, [idNumber]);

  useEffect(() => {
    if (currentGroup) {
      setFetchLoading(true);
      getCompanyUsersApi(String(currentGroup))
        .then((res) => {
          const userOptions = res.data.map((user: any) => ({
            value: user.id,
            label: `${user.Name} ${user.Surname} (${user.Email})`,
          }));
          setUsers(userOptions);
          setFetchLoading(false);
        })
        .catch((err) => {
          console.log("Error fetching users:", err);
          setFetchLoading(false);
        });
    }
  }, [currentGroup]);

  const content = (
    <WizardStepLayout
      action={
        <Form.Item {...tailLayout} style={{ marginBottom: 0 }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Form.Item>
      }
    >
      {(currentUser.Role.Name === "Admin" ||
        currentUser.Role.Name === "Super Admin" ||
        currentUser.Role.Name === "Agent" ||
        currentUser.Role.Name === "Data Capturer") && (
        <Form.Item
          name="PolicyUserID"
          label="Policy User"
          tooltip="Optional: Assign policy to a specific user. If not specified, defaults to the capturing user."
        >
          <Select
            allowClear
            loading={fetchLoading}
            options={users}
            size="large"
            style={{ width: "100%" }}
            placeholder="Select Policy User (Optional)"
            showSearch
            filterOption={(input, option) =>
              String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
      )}
      
      <Form.Item
        name="Identity"
        label="Identity Type"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          options={identityTypeOpt}
          onSelect={(e) => onChangeIdentity(e)}
          style={{ width: "100%" }}
          size="large"
          placeholder="Select Identity"
        />
      </Form.Item>
      {identity === 1 ? (
        <Form.Item
          name="IdNumber"
          label="ID Number"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input
            size="large"
            placeholder="ID Number"
            onChange={(e) => onChangeId(e)}
            maxLength={13}
          />
        </Form.Item>
      ) : (
        <Form.Item
          name="PassportNumber"
          label="Passport Number"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input
            size="large"
            placeholder="Passport Number"
            onChange={onChangeId}
          />
        </Form.Item>
      )}
      <Form.Item
        name="DateOfBirth"
        label="Date Of Birth"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <DatePicker
          id="DateOfBirth"
          placeholder="Date Of Birth"
          size="large"
          style={{ width: "100%" }}
          onChange={(value) => {
            if (value) {
              handleAge(value.toDate());
            }
          }}
        />
      </Form.Item>
      <Form.Item
        name="Gender"
        label="Gender"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          options={genderOpt}
          style={{ width: "100%" }}
          size="large"
          placeholder="Select Gender"
        />
      </Form.Item>
      <Form.Item
        name="Title"
        label="Title"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          options={titleOpt}
          style={{ width: "100%" }}
          size="large"
          placeholder="Select Title"
        />
      </Form.Item>
      <Form.Item
        name="Initials"
        label="Initials"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Initials" />
      </Form.Item>
      <Form.Item
        name="Name"
        label="Name"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input
          size="large"
          placeholder="Name"
          onChange={(e: any) => {
            setPersonalInfo((prevInfo: any) => ({
              ...prevInfo,
              Name: e.target.value,
            }));
          }}
        />
      </Form.Item>
      <Form.Item
        name="Surname"
        label="Surname"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input
          size="large"
          placeholder="Surname"
          onChange={(e: any) => {
            setPersonalInfo((prevInfo: any) => ({
              ...prevInfo,
              Surname: e.target.value,
            }));
          }}
        />
      </Form.Item>

    </WizardStepLayout>
  );

  return content;
};

export default PersonalInfo;
